// components/integrations/shared/CodeExamples.tsx
import React, { useState } from "react";
import { Code, Copy, Check, ChevronDown } from "lucide-react";

export interface EndpointExample {
  key: string;
  label: string;
  method?: "POST" | "GET" | "PUT" | "DELETE";
  endpoint: string;
  body?: Record<string, any>;
}

interface CodeExamplesProps {
  apiKey: string;
  endpoints: EndpointExample[];
}

export const CodeExamples: React.FC<CodeExamplesProps> = ({ apiKey, endpoints }) => {
  const [copied, setCopied] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState<string>(endpoints[0]?.key ?? "");

  const active = endpoints.find((e) => e.key === activeKey) ?? endpoints[0];

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  if (!active) return null;

  const method = active.method ?? "POST";
  const endpoint = active.endpoint;
  const body = active.body ?? {};

  const curlExample = `curl -X ${method} ${endpoint} \\
  -H "X-API-Key: ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(body).replace(/'/g, "'\\''")}'`;

  const jsExample = `fetch('${endpoint}', {
  method: '${method}',
  headers: {
    'X-API-Key': '${apiKey}',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify(${JSON.stringify(body, null, 2)})
})
.then(res => res.json())
.then(data => console.log(data));`;

  const pyExample = `import requests

url = '${endpoint}'
headers = {
    'X-API-Key': '${apiKey}',
    'Content-Type': 'application/json'
}
data = ${JSON.stringify(body, null, 2)}

response = requests.${method.toLowerCase()}(url, headers=headers, json=data)
print(response.json())`;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      {/* Header + Select */}
      <div className="border-b border-slate-200 p-5 bg-gradient-to-r from-slate-50 to-gray-50">
        <div className="flex items-center gap-3 mb-3">
          <Code size={20} className="text-slate-600" />
          <h3 className="font-semibold text-slate-800">Exemples de code</h3>
          <span className="ml-auto text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">
            Copier-coller
          </span>
        </div>

        {/* ✅ Select au lieu des onglets */}
        <div className="relative">
          <select
            value={activeKey}
            onChange={(e) => setActiveKey(e.target.value)}
            className="w-full appearance-none bg-white border border-slate-200 rounded-lg px-4 py-2.5 pr-10 text-sm font-medium text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-800/10 focus:border-slate-400 transition-colors cursor-pointer"
          >
            {endpoints.map((ep) => (
              <option key={ep.key} value={ep.key}>
                {ep.method ?? "POST"} — {ep.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>

        {/* Endpoint actif */}
        <div className="mt-3 text-xs text-slate-500">
          <span className="font-medium text-slate-700 mr-2">{method}</span>
          <code className="bg-slate-100 px-2 py-0.5 rounded text-[11px]">{endpoint}</code>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* cURL */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">cURL</span>
            <button
              onClick={() => copyToClipboard(curlExample, "curl")}
              className="px-3 py-1 bg-slate-100 hover:bg-slate-200 rounded text-xs text-slate-600 flex items-center gap-1 transition-colors"
            >
              {copied === "curl" ? <Check size={14} /> : <Copy size={14} />}
              Copier
            </button>
          </div>
          <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
            <pre className="text-xs text-slate-100 font-mono whitespace-pre-wrap">{curlExample}</pre>
          </div>
        </div>

        {/* JavaScript & Python */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">JavaScript</span>
              <button
                onClick={() => copyToClipboard(jsExample, "js")}
                className="px-3 py-1 bg-slate-100 hover:bg-slate-200 rounded text-xs text-slate-600 flex items-center gap-1 transition-colors"
              >
                {copied === "js" ? <Check size={14} /> : <Copy size={14} />}
                Copier
              </button>
            </div>
            <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto h-[200px]">
              <pre className="text-xs text-slate-100 font-mono whitespace-pre-wrap">{jsExample}</pre>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Python</span>
              <button
                onClick={() => copyToClipboard(pyExample, "py")}
                className="px-3 py-1 bg-slate-100 hover:bg-slate-200 rounded text-xs text-slate-600 flex items-center gap-1 transition-colors"
              >
                {copied === "py" ? <Check size={14} /> : <Copy size={14} />}
                Copier
              </button>
            </div>
            <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto h-[200px]">
              <pre className="text-xs text-slate-100 font-mono whitespace-pre-wrap">{pyExample}</pre>
            </div>
          </div>
        </div>

        <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <span className="font-medium text-amber-700">⚠️ Remplacez</span>
          <span className="ml-1 text-amber-600">
            <code className="bg-amber-100 px-1.5 py-0.5 rounded text-[10px]">VOTRE_CLE_API</code> et
            <code className="bg-amber-100 px-1.5 py-0.5 rounded text-[10px] ml-1">VOTRE_ID_ENTREPRISE</code>
            par vos identifiants
          </span>
        </div>
      </div>
    </div>
  );
};
